import { useEffect, useRef } from "react";

import { cn } from "~/lib/utils";
import { displace, layoutField, rowBase, type Field, type Pointer } from "./RuledSilk.logic";

/**
 * Ruled lines that swell like silk on water and part around the pointer. The
 * ink is the element's `color`. One 2D canvas, paused while offscreen or the
 * tab is hidden. Under reduced motion the lines drift at a third of the speed
 * and the pointer leaves them alone.
 */
const FRAME_MS = 1000 / 60;

export function RuledSilk({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    let field: Field = layoutField(0, 0);
    let dpr = 1;
    let ink = "";
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reduced = motion.matches;
    const pointer: Pointer = { x: 0, y: 0, presence: 0 };
    const target = { x: 0, y: 0, inside: false };
    let t = 0;
    let last = 0;
    let frame = 0;
    let onScreen = true;

    const readInk = () => {
      ink = getComputedStyle(canvas).color;
    };

    const trace = (row: number) => {
      const y0 = rowBase(field, row);
      ctx.beginPath();
      for (let x = 0; ; x = Math.min(field.width, x + field.step)) {
        const y = y0 + displace(field, x, y0, t, pointer);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        if (x >= field.width) break;
      }
      ctx.stroke();
    };

    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, field.width, field.height);
      if (field.rows === 0) return;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = ink;
      ctx.lineWidth = 1;
      for (let row = 0; row < field.rows; row++) {
        // A slow shimmer, so the cloth catches the light rule by rule.
        ctx.globalAlpha = 0.3 + 0.22 * (0.5 + 0.5 * Math.sin(row * 0.7 + t * 0.35));
        trace(row);
      }
      ctx.globalAlpha = 1;
    };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      // Sixty frames a second is plenty for a slow swell, even on a 120Hz display.
      if (last && now - last < FRAME_MS - 1) return;
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      t += dt * (reduced ? 0.33 : 1);
      const follow = 1 - Math.exp(-dt * 6);
      pointer.x += (target.x - pointer.x) * follow;
      pointer.y += (target.y - pointer.y) * follow;
      const wanted = target.inside && !reduced ? 1 : 0;
      pointer.presence += (wanted - pointer.presence) * (1 - Math.exp(-dt * 3));
      draw();
    };

    const start = () => {
      if (frame || !onScreen || document.visibilityState !== "visible") return;
      last = 0;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      field = layoutField(rect.width, rect.height);
      readInk();
      draw();
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const inside = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
      // Arrive where the pointer entered rather than sweeping in from the last exit.
      if (inside && pointer.presence < 0.01) {
        pointer.x = x;
        pointer.y = y;
      }
      target.x = x;
      target.y = y;
      target.inside = inside;
    };
    const onPointerLeave = () => {
      target.inside = false;
    };
    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop());
    const onMotion = () => {
      reduced = motion.matches;
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const intersectionObserver = new IntersectionObserver((entries) => {
      onScreen = entries.at(-1)?.isIntersecting ?? true;
      if (onScreen) start();
      else stop();
    });
    intersectionObserver.observe(canvas);
    // The theme switches by class or attribute on <html>; the ink follows it.
    const themeObserver = new MutationObserver(() => {
      readInk();
      if (!frame) draw();
    });
    themeObserver.observe(document.documentElement, { attributes: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onPointerLeave);
    document.addEventListener("visibilitychange", onVisibility);
    motion.addEventListener("change", onMotion);

    resize();
    start();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      themeObserver.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("pointerleave", onPointerLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      motion.removeEventListener("change", onMotion);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={cn("text-muted-foreground", className)} />;
}
